import React, { ReactElement } from 'react';
import Head from 'next/head';
import useSWR from 'swr';
import { useRouter } from 'next/router';

import { Spinner } from 'components';
import Logo from 'components/Logo';
import WelcomeContent from 'pages/email-verification/WelcomeContent';
import NotFound from 'pages/404/index.page';

import { BasicUserResponse } from 'lib/models/basic-user';

import { get } from 'lib/utils/http';
import { API_ROUTES } from 'lib/api-routes';

import { parseResponse } from 'lib/utils/parser';
import styles from './EmailVerification.module.scss';

enum MESSAGE {
  RECRUITER = "Your e-mail address is successfully confirmed. Now, we need to verify that you're working at this company. This shouldn’t take longer than 1 business day. You’ll be notified when your account is ready to go!",
  MANAGER = "Your e-mail address is successfully confirmed. Now, we need to verify that you're authorized to create an account for this Student Society. This shouldn’t take longer than 1 business day. You’ll be notified when your account is ready to go!",
}

const EmailVerification = (): ReactElement => {
  const router = useRouter();
  const { userId } = router.query;
  if (!userId) {
    return <NotFound />;
  }

  const pathname = userId ? `${API_ROUTES.USER_CONFIRMATION}/${userId}` : null;
  const { data: basicUserResponse, error } = useSWR<BasicUserResponse>(
    pathname,
    get
  );

  const isLoading = userId && !(basicUserResponse || error);
  const basicUserAttributes = parseResponse(basicUserResponse);

  const type = basicUserAttributes?.userable_type;

  return (
    <>
      <Head>
        <title>Email Verification | Huzzle</title>
        <meta name="description" content="Huzzle" />
      </Head>
      <section className={styles.container} data-testid="EmailVerification">
        <div className={styles.content}>
          {type === 'Recruiter' && (
            <Logo name="huzzle.recruiter" size="large" color="black" />
          )}
          {type === 'Manager' && (
            <Logo name="huzzle.society" size="large" color="black" />
          )}
        </div>
        {isLoading ? (
          <Spinner size="large" wrapper />
        ) : basicUserAttributes?.is_confirmed ? (
          <WelcomeContent
            message={
              basicUserAttributes?.userable_type === 'Manager'
                ? MESSAGE.MANAGER
                : MESSAGE.RECRUITER
            }
            usertype={type}
          />
        ) : (
          userId && <p className={styles.info}>Please confirm your email.</p>
        )}
      </section>
    </>
  );
};

export default EmailVerification;
